import { HttpClient, HttpContext, HttpErrorResponse } from '@angular/common/http';
import { Injectable, signal, inject } from '@angular/core';
import { Router } from '@angular/router';
import { applyTransaction } from '@datorama/akita';
import { MessageService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import {
  catchError,
  concatMap,
  debounceTime,
  finalize,
  first,
  forkJoin,
  map,
  Observable,
  of,
  take,
  tap,
  throwError,
  filter,
  BehaviorSubject,
  Subject,
} from 'rxjs';

import { ConfirmationModalComponent } from '../../core/components/confirmation-modal/confirmation-modal.component';
import { FailedToLoadModalAction, FailedToLoadModalComponent } from '../../core/components/failed-to-load-modal/failed-to-load-modal.component';
import { IntegratedLoginContext, SessionQuery, SessionStore } from '../../core/state';
import { FeatureFlagRepository, FeatureName } from '../../core/state/feature-flag.repository';
import { LoanApplicationService } from '../../disclosure/services/loan-application.service';
import { BorrowersStore } from '../../disclosure/state/borrowers/borrowers.store';
import {
  AusQuery,
  AusStore,
  Cmg1003ApplicationInfoStore,
  Cmg1003AssetsQuery,
  Cmg1003AssetsStore,
  Cmg1003BorrowerQuery,
  Cmg1003BorrowerStore,
  Cmg1003CreditSummaryStore,
  Cmg1003DeclarationsQuery,
  Cmg1003DeclarationsStore,
  Cmg1003DocumentSummaryStore,
  Cmg1003EmploymentExpensesQuery,
  Cmg1003EmploymentExpensesStore,
  Cmg1003EmploymentIncomeExpensesQuery,
  Cmg1003EmploymentIncomeExpensesStore,
  Cmg1003FileDataQuery,
  Cmg1003FileDataStore,
  Cmg1003GmiQuery,
  Cmg1003GmiStore,
  Cmg1003LoanOfficerQuery,
  Cmg1003LoanOfficerStore,
  Cmg1003LoanQuery,
  Cmg1003LoanStore,
  Cmg1003ProcessingQuery,
  Cmg1003ProcessingStore,
  Cmg1003PropertyQuery,
  Cmg1003PropertyStore,
  Cmg1003StoreQueryService,
  Cmg1003TransactionQuery,
  Cmg1003TransactionStore,
} from '../../loan-state';
import { LoanNotesQuery } from '../../loan-state/loan-notes/loan-notes.query';
import { LoanNotesDataStore } from '../../loan-state/loan-notes/loan-notes.store';
import { RetailBridgeService } from '../../shared/services/retail-bridge.service';
import { SpinnerService } from '../../shared/services/spinner.service';
import { isRequiredSaveField } from '../../shared/utils/validation.util';
import { ManageBorrowerPayload } from '../components/borrower-information/manage-borrowers-modal/manage-borrower.model';
import { CmgUiUtilities } from '../components/common/cmg-ui-utils';
import {
  BorrowerModel,
  Cmg1003SectionTypes,
  InvalidControlModel,
  LoanDataModel,
  LoanPurposeType,
  MortgageType,
  MsaCounties,
  OriginationChannel,
  PropertyTaxCalculationType,
  RefinancePurposeFha,
  loanStatusInfo,
} from '../models';
import { UpsertSnapshotEntryRequest, UpsertSnapshotEntryResponse } from '../models/auto-recovery.model';
import { Cmg1003Api } from '../models/cmg-1003-common.enum';
import { Cmg1003Model } from '../models/cmg-1003.model';
import { DocumentSummaryModel } from '../models/document-summary.model';
import { LeadNote, LoanApplicationNote, LoanNotesModel } from '../models/notes.model';
import { Cmg1003ConstantsService } from './cmg-1003-constants.service';
import { ApiErrorCodes, PageNotAllowedActions, PageNotAllowedParams } from '../../models';
import { InternetDisconnectService } from '../../services/internet-disconnect.service';
import { PostProcessingUrlaUiService } from '../../loan-state/post.processing.urla.ui.service';
import { UrlaSnapshotHistoryRow } from '../models/urla-snapshot-history-row';
import { Cmg1003CoverLetterSubmissionsQuery } from '../../loan-state/cover-letter-submissions/cover-letter-submissions.query';
import { Cmg1003CoverLetterSubmissionsStore } from '../../loan-state/cover-letter-submissions/cover-letter-submissions.store';
import { Cmg1003LoanStatusHistoryStore } from '../../loan-state/loan-status-history/loan-status-history.store';
import { Cmg1003LoanStatusHistoryQuery } from '../../loan-state/loan-status-history/loan-status-history.query';
import { REQUIRES_SECURITY_PROFILE } from '../../interceptors';
import { LoanApplicationQuery } from '../../disclosure/state/loan-application/loan-application.query';
import { SupplementalSectionPromptsQuery } from '../../disclosure/state/supplemental-section-prompts/supplemental-section-prompts.query';
import { Prompt } from '../../disclosure/models/loan-application.model';
import { getPropertyTaxCalculationType } from '../components/employment-income-expenses/expenses/expenses.util';
import { Cmg1003FundingReviewStore } from '../../loan-state/funding-review/funding-review.store';
import { DisclosureApplicationStore } from '../../disclosure/state/disclosure-application/disclosure-application.store';
import { DisclosureApplicationQuery } from '../../disclosure/state/disclosure-application/disclosure-application.query';
import { PartyMiscStore } from '../../loan-state/party-misc/party-misc.store';
import { APP_ENV } from 'apps/cmg-clear2-ui/src/environments/environment.model';

export interface IApplicationStatus {
  applicationId: number;
  isActive: boolean;
}

@Injectable({ providedIn: 'root' })
export class Cmg1003Service {
  private readonly _router = inject(Router);
  private readonly _httpClient = inject(HttpClient);
  private readonly _applicationInfoStore = inject(Cmg1003ApplicationInfoStore);
  private readonly _assetLiabilitiesStore = inject(Cmg1003AssetsStore);
  private readonly _assetsQuery = inject(Cmg1003AssetsQuery);
  private readonly _borrowerStore = inject(Cmg1003BorrowerStore);
  private readonly _docutechBorrowerStore = inject(BorrowersStore);
  private readonly _declarationsStore = inject(Cmg1003DeclarationsStore);
  private readonly _employmentExpensesStore = inject(Cmg1003EmploymentExpensesStore);
  private readonly _employmentExpensesQuery = inject(Cmg1003EmploymentExpensesQuery);
  private readonly _employmentIncomeExpensesStore = inject(Cmg1003EmploymentIncomeExpensesStore);
  private readonly _employmentIncomeExpensesQuery = inject(Cmg1003EmploymentIncomeExpensesQuery);
  private readonly _fileDataQuery = inject(Cmg1003FileDataQuery);
  private readonly _fileDataStore = inject(Cmg1003FileDataStore);
  private readonly _gmiStore = inject(Cmg1003GmiStore);
  private readonly _loanStore = inject(Cmg1003LoanStore);
  private readonly _loanQuery = inject(Cmg1003LoanQuery);
  private readonly _loanOfficerStore = inject(Cmg1003LoanOfficerStore);
  private readonly _loanOfficerQuery = inject(Cmg1003LoanOfficerQuery);
  private readonly _propertyStore = inject(Cmg1003PropertyStore);
  private readonly _processingQuery = inject(Cmg1003ProcessingQuery);
  private readonly _processingStore = inject(Cmg1003ProcessingStore);
  private readonly _propertyQuery = inject(Cmg1003PropertyQuery);
  private readonly _sessionStore = inject(SessionStore);
  private readonly _sessionQuery = inject(SessionQuery);
  private readonly _storeQuery = inject(Cmg1003StoreQueryService);
  private readonly _transactionStore = inject(Cmg1003TransactionStore);
  private readonly _transactionQuery = inject(Cmg1003TransactionQuery);
  private readonly _declarationsQuery = inject(Cmg1003DeclarationsQuery);
  private readonly _gmiQuery = inject(Cmg1003GmiQuery);
  private readonly _borrowerQuery = inject(Cmg1003BorrowerQuery);
  private readonly _messageService = inject(MessageService);
  private readonly _retailBridgeService = inject(RetailBridgeService);
  private readonly _documentSummaryStore = inject(Cmg1003DocumentSummaryStore);
  private readonly _creditSummaryStore = inject(Cmg1003CreditSummaryStore);
  private readonly _ausStore = inject(AusStore);
  private readonly _ausQuery = inject(AusQuery);
  private readonly _loanNotesStore = inject(LoanNotesDataStore);
  private readonly _loanNotesQuery = inject(LoanNotesQuery);
  private readonly _spinnerService = inject(SpinnerService);
  private readonly _disclosuresLoanApplicationService = inject(LoanApplicationService);
  private readonly _dialogService = inject(DialogService);
  private readonly _featureFlagsRepository = inject(FeatureFlagRepository);
  private readonly _cmg1003ConstantsService = inject(Cmg1003ConstantsService);
  private readonly _internetDisconnectService = inject(InternetDisconnectService);
  private readonly _postProcessingUrlaUiService = inject(PostProcessingUrlaUiService);
  private readonly _coverLetterSubmissionsQuery = inject(Cmg1003CoverLetterSubmissionsQuery);
  private readonly _coverLetterSubmissionsStore = inject(Cmg1003CoverLetterSubmissionsStore);
  private readonly _loanStatusHistoryStore = inject(Cmg1003LoanStatusHistoryStore);
  private readonly _loanStatusHistoryQuery = inject(Cmg1003LoanStatusHistoryQuery);
  private readonly _loanApplicationQuery = inject(LoanApplicationQuery);
  private readonly _supplementalSectionPromptsQuery = inject(SupplementalSectionPromptsQuery);
  private readonly _empIncExpQuery = inject(Cmg1003EmploymentIncomeExpensesQuery);
  private readonly _fundingReviewStore = inject(Cmg1003FundingReviewStore);
  private readonly _disclosureAppStore = inject(DisclosureApplicationStore);
  private readonly _disclosureAppQuery = inject(DisclosureApplicationQuery);
  private readonly _partyMiscStore = inject(PartyMiscStore);
  private readonly _environment = inject(APP_ENV);

  private _fileDataId: number;

  isSaving1003$ = new BehaviorSubject(false);
  isUrlaInitialized$ = new BehaviorSubject(false);
  isMobileLoanDetailsExpanded = signal(false);

  refinanceTypeChanged$ = new BehaviorSubject<boolean>(false);

  private readonly _validateAndSave1003$: Subject<any> = new Subject();
  validateAndSave1003$: Observable<any> = this._validateAndSave1003$.asObservable();
  private readonly _saveCoverLetterSubmissionsSub: Subject<any> = new Subject();
  saveCoverLetterSubmissions$: Observable<any> = this._saveCoverLetterSubmissionsSub.asObservable();

  validations$ = this._sessionQuery
    .select((s) => s.validationWarnings)
    .pipe(
      debounceTime(100),
      map((validationWarnings) => {
        const validations: InvalidControlModel[] = [];

        if (validationWarnings && validationWarnings.length > 0) {
          // Get a flat list of validations to display in UI.
          validationWarnings.forEach((validationFormGroup) => {
            validationFormGroup.invalidControls.forEach((control) => {
              validations.push(control);
            });
          });
        }

        return validations;
      }),
    );

  /**
   * Invalid / Required Fields that prevent Save Loan when not corrected
   */
  validationSaveErrors$ = this._sessionQuery
    .select((s) => s.validationErrors)
    .pipe(
      debounceTime(100),
      map((validationErrors) => {
        const validations: InvalidControlModel[] = [];

        if (validationErrors && validationErrors.length > 0) {
          validationErrors.forEach((validationFormGroup) => {
            validationFormGroup.invalidControls.forEach((control) => {
              if (isRequiredSaveField(control.controlId)) {
                validations.push(control);
              }
            });
          });
        }
        return validations;
      }),
    );

  isSandboxLoanMVP2Enabled = false;

  constructor() {
    this._featureFlagsRepository
      .isFeatureEnabled$(FeatureName.SandboxLoanMVP2)
      .pipe(take(1))
      .subscribe((enabled) => {
        this.isSandboxLoanMVP2Enabled = enabled;
      });
  }

  getValidationCountOfType(sectionType: Cmg1003SectionTypes) {
    return this._sessionQuery.select().pipe(
      debounceTime(100),
      map((session) => {
        let count = 0;

        for (let i = 0; i < session.validationWarnings?.length; i++) {
          if (session.validationWarnings[i].section === sectionType) {
            // Don't break from loop because Sections like Borrower or Loan will have multiple subsections so need to sum up the invalidControls.
            count += session.validationWarnings[i]?.invalidControls?.length;
          }
        }

        return count;
      }),
    );
  }

  deleteCoBorrower = (fileDataId: number, applicationId: number, borrowerId: number) =>
    this._httpClient.delete(`${this._environment.retail.apiUrl}/all1003/file/${fileDataId}/application/${applicationId}/borrower/${borrowerId}`, {
      context: new HttpContext().set(REQUIRES_SECURITY_PROFILE, true),
    });

  get1003$ = () => {
    const appIdsStatusMap = this._storeQuery.getAllApplicationsWithStatus();
    return this._get1003$(appIdsStatusMap);
  };

  /**
   * Gets the All1003 data for the file AND the cached version if available.
   *
   * Cached version:
   * 1) Represents the latest version, if multiple people worked on the same file.
   * 2) Data has not yet been committed to Byte.
   * 3) If it doesn't exist, API will return it as null (NOT error).
   */
  private _get1003$(appIdStatusMap?: IApplicationStatus[]) {
    const all1003Url = `${this._environment.retail.apiUrl}/all1003/file/${this._fileDataId}`;
    const documentSummaryUrl = `${all1003Url}/documentsummary`;

    this.isUrlaInitialized$.next(false); // Save Loan button will be disabled while isUrlaInitialized$ = false
    this._spinnerService.show();

    return forkJoin([
      this._httpClient
        .get<Cmg1003Model>(all1003Url, { context: new HttpContext().set(REQUIRES_SECURITY_PROFILE, true) })
        .pipe(catchError((err) => this._on1003Error(err, true))),
      this._httpClient.get<DocumentSummaryModel>(documentSummaryUrl).pipe(catchError(this._onDocumentSummaryError)),
    ]).pipe(
      map(([all1003Model, documentSummary]) => {
        this.on1003Received(all1003Model, appIdStatusMap);

        if (this._environment.retail.enableAutoRecovery) {
          this._awaitAutoRecoveryDecision(all1003Model);
        }
        !documentSummary ? this._documentSummaryStore.reset() : this._documentSummaryStore.update(documentSummary);

        if (all1003Model) {
          this._featureFlagsRepository
            .isFeatureEnabled$(FeatureName.Disclosures)
            .pipe(take(1))
            .subscribe((isDisclosuresFeatureEnabled) => {
              if (isDisclosuresFeatureEnabled) {
                this._disclosuresLoanApplicationService.getLoanApplication(all1003Model.fileDataId).subscribe();
              }
            });
        }
        this._spinnerService.hide();

        return all1003Model;
      }),
      concatMap((loan) => this._sessionQuery.getMsaCountiesBystate(loan?.property?.state)),
    );
  }

  private _getErrorMessageAndTitle(apiErrorCode: ApiErrorCodes) {
    const isIntegratedLogin = this._sessionStore.getValue()?.integratedLoginContext === IntegratedLoginContext.Byte;
    const messageTitle = {
      message: `<span>Your security profile does not have access to this loan. ${isIntegratedLogin ? '<br/><br/>Close this screen by clicking on the X in the top right corner of this browser window.' : ''}</span>`,
      emailText:
        '<span><i class="pi pi-envelope"></i> <a href="mailto:ClearSupport@cmgfi.com?subject=Requesting access to Loan">ClearSupport&#64;cmgfi.com</a></span>',
    };
    switch (apiErrorCode) {
      case ApiErrorCodes.MISSING_BACKGROUND_CHECK:
        messageTitle.message =
          '<span>Your profile is not approved to open or view Georgia loans. Please reach out to <a href="mailto:recruiting@cmgfi.com?subject=Access to Georgia Loans">recruiting@cmgfi.com</a> for access. Note: Access approval takes 48 to 72 hours once Recruiting is contacted.</span>';
        messageTitle.emailText =
          '<span><i class="pi pi-envelope"></i> <a href="mailto:ClearSupport@cmgfi.com?subject=Access to Georgia Loans">ClearSupport&#64;cmgfi.com</a></span>';
        return messageTitle;
      default:
        return messageTitle;
    }
  }

  private _on1003Error(error: HttpErrorResponse, showModal = false) {
    const isIntegratedLogin = this._sessionStore.getValue()?.integratedLoginContext === IntegratedLoginContext.Byte;
    if (showModal) {
      if (error.status === 403) {
        const messageTitle = this._getErrorMessageAndTitle(error?.error?.Code);
        const phoneSpan = '<span><i class="pi pi-phone"></i> <b>(833) 243-4264</b></span>';
        const helpUrlSpan =
          '<span><i class="pi pi-desktop"></i> <a href="https://helpdesk.cmgmortgage.com" target="_blank">helpdesk.cmgmortgage.com</a></span>';
        const dialogRef = this._dialogService.open(ConfirmationModalComponent, {
          closable: false,
          draggable: true,
          keepInViewport: true,
          width: '500px',
          styleClass: 'confirmation-modal-with-support-links',
          contentStyle: { 'max-height': '500px', overflow: 'auto' },
          baseZIndex: this._cmg1003ConstantsService.modalZIndex,
          data: {
            message:
              '<div class="d-flex flex-wrap flex-column p-3">' +
              messageTitle.message +
              '<br>' +
              phoneSpan +
              messageTitle.emailText +
              helpUrlSpan +
              '</div>',
            header: 'Unauthorized Access',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Return to Pipeline',
            acceptVisible: !isIntegratedLogin,
            rejectLabel: null,
          },
        });

        dialogRef.onClose.pipe(first()).subscribe(() => {
          if (!isIntegratedLogin) {
            window.location.href = '/pipeline';
          }
        });
      } else {
        const loanId = this._sessionStore.getValue()?.cached1003?.snapshot?.Value?.loanInformation?.fileName;
        this._dialogService.open(FailedToLoadModalComponent, {
          header: 'Page Failed To Load Correctly',
          width: '512px',
          data: { loanId, action: isIntegratedLogin ? FailedToLoadModalAction.None : FailedToLoadModalAction.GoToPipeline },
          closable: false,
          draggable: true,
          keepInViewport: true,
          styleClass: 'dialog-content-overflow-hidden',
        });
      }
    }
    return of(null);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private _onDocumentSummaryError(error: HttpErrorResponse) {
    return of(null);
  }

  manageBorrowers(payload: ManageBorrowerPayload) {
    this._spinnerService.show('Saving loan...');
    const endPoint = `${this._environment.retail.apiUrl}/all1003/file/${this._fileDataId}/manageborrowers`;
    return this._httpClient.post(endPoint, payload, { context: new HttpContext().set(REQUIRES_SECURITY_PROFILE, true) }).pipe(
      take(1),
      finalize(() => {
        this._spinnerService.hide();
      }),
    );
  }

  updateBorrowerCreditAuthorization(borrower: BorrowerModel, okToPullCredit: boolean) {
    const borrowerName = `${borrower?.firstName} ${borrower?.middleName} ${borrower?.lastName} ${borrower?.suffix}`;
    this._spinnerService.show(`Saving ${borrowerName} Credit Pull Authorization...`);
    const endPoint = `${this._environment.retail.apiUrl}/all1003/file/${this._fileDataId}/borrower/${borrower?.borrowerId}`;
    return this._httpClient.post(endPoint, { okToPullCredit }, { context: new HttpContext().set(REQUIRES_SECURITY_PROFILE, true) }).pipe(
      take(1),
      finalize(() => {
        this._spinnerService.hide();
      }),
    );
  }

  /**
   * Performs POST request to save 1003 form, based on store values.
   * Note: If multiple people are working on the same file, last save wins - i.e. data can be lost by being overwritten.
   */
  save1003(callbackFn?: any, spinnerMessage?: string) {
    if (this.isSaving1003$.value === true) {
      return;
    } else {
      this.isSaving1003$.next(true);
    }
    try {
      this._spinnerService.show(spinnerMessage);
      const updateEndpoint = `${this._environment.retail.apiUrl}/all1003`;
      const activeApplicationId = this._storeQuery.getActiveApplicationId();
      const appStatusMap = this._storeQuery.getAllApplicationsWithStatus();
      const borrowerIds = this._storeQuery.getAllBorrowerIdsFromCurrentApplications(activeApplicationId);
      this._saveFirstTimeHomeBuyerStatusToFileData();
      this._updateAntiSteeringByLoanChannel();

      let loanApplication = { ...this._loanApplicationQuery.getValue() };
      const supplementalPrompts: Prompt[] = this._supplementalSectionPromptsQuery.getAll();

      if (loanApplication?.supplemental?.sections) {
        loanApplication = {
          ...loanApplication,
          supplemental: {
            ...loanApplication.supplemental,
            sections: this._disclosuresLoanApplicationService.getUpdatedSupplementalSection(loanApplication, supplementalPrompts),
          },
        };
      }

      const disclosureApplications = this._disclosureAppQuery.getAll();
      const loanInformation = this._loanQuery.assembleForSave();
      const property = this._propertyQuery.assemblePropertyForSave(borrowerIds);

      this._featureFlagsRepository
        .isFeatureEnabled$(FeatureName.Disclosures)
        .pipe(take(1))
        .subscribe((isDisclosuresFeatureEnabled) => {
          if (isDisclosuresFeatureEnabled) {
            loanInformation.vaSplitFee = loanApplication.vaSplitFee === 'Yes' ? true : false;
            property.isNewConstruction = loanApplication.newConstruction === 'Yes' ? true : false;
          }
        });

      // the new Api for calculation should be called right after PUT all1003
      const byteFill = this._gvtFundingFactorsFieldsChanged(loanInformation);

      // Note: Parts of the payload is borrower-specific.
      const all1003Payload = {
        fileDataId: this._fileDataId,
        applications: this._storeQuery.getAllApplications(),
        assetsLiabilities: this._assetsQuery.getPersistentState(activeApplicationId),
        borrowers: this._borrowerQuery.getAll({
          filterBy: (b) => borrowerIds.some((bId) => bId === b.borrowerId),
        }),
        declarations: this._declarationsQuery.getValueByBorrowerIds(borrowerIds),
        employmentExpenses: this._employmentExpensesQuery.getValue(),
        employmentIncomeExpenses: [this._employmentIncomeExpensesQuery.assembleForSave(activeApplicationId)],
        fileData: this._fileDataQuery.getValue(),
        governmentMonitors: this._gmiQuery.getValueByBorrowerIds(borrowerIds),
        loanInformation: loanInformation,
        loanOfficer: this._loanOfficerQuery.getValue(),
        property: property,
        processing: this._processingQuery.getValue(),
        transactionInfo: this._transactionQuery.assembleForSave(),
        aus: this._ausQuery.getValue(),
        coverLetterSubmissionNotes: this._coverLetterSubmissionsQuery.getValue(),
        dirtySections: this._storeQuery.getDirtySections(),
        Supplemental: loanApplication.supplemental,
        ...(disclosureApplications !== null && { disclosureApplications }),
      };

      // Remove incomes with negative incomeID and no amount to prevent saving as new income
      const filteredPayload = {
        ...all1003Payload,
        employmentIncomeExpenses: all1003Payload.employmentIncomeExpenses.map((expense) => {
          // Filter the grossMonthlyIncomes array within each expense
          const filteredIncomes = expense.grossMonthlyIncomes?.filter((income) => {
            return !(income.incomeId < 0 && (income.amount === null || income.amount === undefined));
          });

          // Return the modified expense object with the filtered grossMonthlyIncomes
          return {
            ...expense,
            grossMonthlyIncomes: filteredIncomes,
          };
        }),
      };

      forkJoin([
        this._httpClient.patch(updateEndpoint, filteredPayload, {
          context: new HttpContext().set(REQUIRES_SECURITY_PROFILE, true),
          params: {
            byteFill,
          },
        }),
        this.saveLoanNotes(),
      ])
        .pipe(
          map(([all1003Model]) => all1003Model),
          catchError((e) => {
            this.isSaving1003$.next(false);
            return this._handleError(e, Cmg1003Api.Save1003);
          }),
        )
        .subscribe((all1003Model: Cmg1003Model) => {
          try {
            this.resetStores();

            this.getLoanNotes(all1003Model.fileDataId).subscribe();
            /**
             * Stores have been reset, but need to retain the active application Id
             * to set in _on1003Received. In case of a new application, the application id is
             * temporary(negative value), on a successful save the id is replaced by byte provided id.
             * One way to keep track of this is to mark the ids that should not be active.
             */
            this._on1003Saved(all1003Model, appStatusMap);
            this._messageService.add({ severity: 'success', summary: '', detail: 'Data saved successfully!' });

            // Post save
            this._featureFlagsRepository
              .isFeatureEnabled$(FeatureName.Disclosures)
              .pipe(take(1))
              .subscribe((isDisclosuresFeatureEnabled) => {
                if (isDisclosuresFeatureEnabled) {
                  this._disclosuresLoanApplicationService.refreshDisclosureState().subscribe();
                }
              });

            if (callbackFn && typeof callbackFn === 'function') {
              callbackFn();
            }
          } catch (error) {
            this.isSaving1003$.next(false);
            this._spinnerService.hide();
            throw error;
          }
        });
    } catch (error) {
      this.isSaving1003$.next(false);
      this._spinnerService.hide();
      throw error;
    }
  }

  private _gvtFundingFactorsFieldsChanged(loan: LoanDataModel): boolean {
    const {
      mortgageAppliedFor,
      loanPurpose,
      baseLoanAmount,
      salesPrice,
      loanTerm,
      mipffFinancingCalcOption,
      interestRate,
      fhaExpectedEndorsementDate,
      sectionOfAct,
      caseNumberAssignedDate,
      appraisalValue,
      refinancePurposeFha,
      fhaPreviousEndorsementDate,
      fundingFeeType,
      refinancePurposeVa,
    } = loan;

    const loanInitialValue = this._loanStore.initialValue;
    const validMortgageTypes = [MortgageType.fha, MortgageType.va, MortgageType.usda];
    const isFha = mortgageAppliedFor === MortgageType.fha;
    const isVa = mortgageAppliedFor === MortgageType.va;
    const isUsda = mortgageAppliedFor === MortgageType.usda;
    const isRefinance = loanPurpose === LoanPurposeType.refinance;

    const fhaVaUsdaRequiredFieldsChanged =
      baseLoanAmount !== loanInitialValue?.baseLoanAmount ||
      salesPrice !== loanInitialValue?.salesPrice ||
      loanTerm !== loanInitialValue?.loanTerm ||
      loanPurpose !== loanInitialValue?.loanPurpose ||
      mipffFinancingCalcOption !== loanInitialValue?.mipffFinancingCalcOption;

    const fhaRequiredFieldsChanged =
      interestRate !== loanInitialValue?.interestRate ||
      fhaExpectedEndorsementDate !== loanInitialValue?.fhaExpectedEndorsementDate ||
      sectionOfAct !== loanInitialValue?.sectionOfAct ||
      caseNumberAssignedDate !== loanInitialValue?.caseNumberAssignedDate ||
      appraisalValue !== loanInitialValue?.appraisalValue;

    const isStreamlinedRefinance =
      refinancePurposeFha === RefinancePurposeFha.StreamlinedWithAppraisal || refinancePurposeFha === RefinancePurposeFha.StreamlinedWithoutAppraisal;

    return (
      loan?.mortgageAppliedFor !== loanInitialValue?.mortgageAppliedFor ||
      (validMortgageTypes.includes(mortgageAppliedFor) && fhaVaUsdaRequiredFieldsChanged) ||
      (isFha && fhaRequiredFieldsChanged) ||
      (isFha && isRefinance && refinancePurposeFha !== loanInitialValue?.refinancePurposeFha) ||
      (isFha && isRefinance && isStreamlinedRefinance && fhaPreviousEndorsementDate !== loanInitialValue?.fhaPreviousEndorsementDate) ||
      (isVa && fundingFeeType !== loanInitialValue?.fundingFeeType) ||
      (isVa && isRefinance && refinancePurposeVa !== loanInitialValue?.refinancePurposeVa) ||
      (isUsda && this._fileDataQuery?.getValue()?.rhsCaseNoAssignmentPeriod !== this._fileDataStore?.initialValue?.rhsCaseNoAssignmentPeriod)
    );
  }

  validateAndSave1003(successCallbackFn: any): void {
    this._validateAndSave1003$.next(successCallbackFn);
  }

  saveCoverLetterSubmissionsIn1003(successCallbackFn: any) {
    this._saveCoverLetterSubmissionsSub.next(successCallbackFn);
  }

  on1003Received = (all1003Model: Cmg1003Model, applicationIds?: IApplicationStatus[]) => {
    applyTransaction(() => {
      this._spinnerService.show();

      // TODO: Revise activeId, so that we stay on the same app, even after temporary applicationId (i.e. negative applicationId) becomes a real ID.
      this._applicationInfoStore.set(all1003Model?.applications);
      this._borrowerStore.add(all1003Model?.borrowers);

      // TODO - Should not need two borrower stores. See if we can remove the docutech-borrowers-store.
      // For now we must update the docutech-borrowers-store to pick up any changes in the cmg-1003-borrower-store.
      all1003Model?.borrowers.forEach((b) => {
        this._docutechBorrowerStore.update(b.borrowerId, {
          email: b.email,
          firstName: b.firstName,
          homePhone: b.homePhone,
          lastName: b.lastName,
          middleName: b.middleName,
        });
      });

      this._declarationsStore.add(all1003Model?.declarations);
      if (all1003Model?.employmentExpenses?.voe && all1003Model?.features) {
        all1003Model.employmentExpenses.voe = { ...all1003Model.features };
      }
      this._employmentExpensesStore.preInitialize(all1003Model?.employmentExpenses);
      this._employmentIncomeExpensesStore.add(all1003Model?.employmentIncomeExpenses);
      this._fileDataStore.preInitialize(all1003Model?.fileData);
      this._gmiStore.add(all1003Model?.governmentMonitors);

      // [Byte]: Upon file upload, Byte auto-checks "TBD" to prevent a RESPA-complete from being auto-converted from Prospect to LE Pending.
      // [Clear UI]: However, if an address is provided, we force "TBD" checkbox to be unchecked. (Matches Clear 1 behavior)
      this._propertyStore.update({
        ...all1003Model?.property,
        isPropertyAddressTBD: !all1003Model?.property?.street || all1003Model?.property?.street?.trim()?.toLowerCase() === 'tbd',
      });
      this._propertyStore.preInitialize();

      this._loanOfficerStore.preInitialize(all1003Model?.loanOfficer);
      this._transactionStore.preInitialize(all1003Model?.transactionInfo);
      this._loanStore.preInitialize({ ...all1003Model?.loanInformation, isCrossBusinessEntity: all1003Model?.isCrossBusinessEntity });
      this._assetLiabilitiesStore.add(all1003Model?.assetsLiabilities);
      this._ausStore.preInitialize(all1003Model);
      this._creditSummaryStore.preInitialize(all1003Model?.creditSummary);
      this._sessionStore.preInitialize(all1003Model);
      this._applicationInfoStore.setActive(
        applicationIds
          ? this._calculateActiveApplicationId(
              applicationIds,
              all1003Model?.applications.map((t) => t.applicationId),
            )
          : all1003Model?.applications[0].applicationId,
      );

      this._coverLetterSubmissionsStore.preInitialize(all1003Model?.coverLetterSubmissionNotes);
      this._fundingReviewStore.preInitialize(all1003Model?.fundingReview);
      this._processingStore.preInitialize(all1003Model?.processing);
      this._partyMiscStore.preInitialize(all1003Model?.partyMisc);

      setTimeout(() => {
        this._storeQuery.setHead();
        this._postProcessingUrlaUiService.reset();
      }, 0);
    });

    // Call storeQuery.setHead one more time after URLA is fully initialized.
    // Using debounceTime with 2 seconds ensures we call storeQuery.setHead only after all of the components within the URLA have
    // had time to receive any subscriptions updates and make their own store updates as well.
    // All URLA related stores will update SessionStore.lastUpdatedDateTime upon any update, so here we observe
    // when lastUpdatedDateTime has a period of idleness to determine when the URLA has fully initialized.
    this._sessionQuery
      .select((s) => s.lastUpdatedDateTime)
      .pipe(
        debounceTime(2000),
        take(1),
        tap(() => {
          this._storeQuery.setHead();
          this.isUrlaInitialized$.next(true);
          this._spinnerService.reset();
        }),
      )
      .subscribe();

    setTimeout(() => {
      // give 1 event cycle to let the update finish
      this.isSaving1003$.next(false);
      this._sessionStore.update({ all1003Loaded: true });
    }, 250);
  };

  reset() {
    this.reset$().subscribe();
  }

  reset$() {
    const appIdsStatusMap = this._storeQuery.getAllApplicationsWithStatus();
    this.resetStores();
    /**
     * Stores have been reset, but need to retain the active application Id
     * to set in _on1003Received
     */
    return this._get1003$(appIdsStatusMap);
  }

  resetStores() {
    this._sessionStore.update({ all1003Loaded: false, shouldReload1003: false, validationErrors: [], validationWarnings: [] });
    applyTransaction(() => {
      this._applicationInfoStore.reset();
      this._borrowerStore.reset();
      this._declarationsStore.reset();
      this._employmentExpensesStore.reset();
      this._employmentIncomeExpensesStore.reset();
      this._fileDataStore.reset();
      this._gmiStore.reset();
      this._propertyStore.reset();
      this._loanOfficerStore.reset();
      this._transactionStore.reset();
      this._loanStore.reset();
      this._assetLiabilitiesStore.reset();
      this._documentSummaryStore.reset();
      this._creditSummaryStore.reset();
      this._ausStore.reset();
      this._loanNotesStore.reset();
      this._coverLetterSubmissionsStore.reset();
      this._loanStatusHistoryStore.reset();
      this._fundingReviewStore.reset();
      this._disclosureAppStore.reset();
    });
  }

  private readonly _handleError = (error: any, api: Cmg1003Api) => {
    let errorMessage = '';
    if (error.error instanceof ErrorEvent) {
      // Get client-side error
      errorMessage = error.error.message;
    } else {
      // Get server-side error
      errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
    }

    if (!this._internetDisconnectService.isInternetDisconnected(error?.status)) {
      this._sessionStore.update({
        cmg1003Message: {
          data: {
            httpErrorResponse: error,
            api,
          },
        },
      });
    } else {
      this._internetDisconnectService.showInternetDisconnectModal();
    }

    return throwError(errorMessage);
  };

  private _on1003Saved(all1003Model: Cmg1003Model, applicationIds: IApplicationStatus[]) {
    this.on1003Received(all1003Model, applicationIds);
  }

  /**
   * _calculateActiveApplicationId tries to find the applicationId that was saved. A new application when created on client
   * side has a negative id. This negative Id gets replaced with a positive one after save by the API. API returns the new Ids back.
   * As a result, this negative id is lost. One way to re-map it is to find the applications that existed before the save and
   * eliminite them as active applications, leaving us with the new application. This may not work in situations where multiple
   * applications are saved simultaneously by different users. However it is a very remote edge case, as adding new application
   * it self is not very common.
   * Deleted Application situation: If the last active application is not found in the list of applications, first application
   * becomes default application.
   * @param applicationIdsWithStatus
   * @param applicationIds
   */
  private _calculateActiveApplicationId(applicationIdsWithStatus: IApplicationStatus[], applicationIds: number[]): number {
    let defaultActiveApplicationId = applicationIds[0];
    const activeApplicationBeforeSave = applicationIdsWithStatus.find((t) => t.isActive);
    if (
      activeApplicationBeforeSave &&
      activeApplicationBeforeSave.applicationId > 0 &&
      applicationIds.some((a) => a === activeApplicationBeforeSave.applicationId)
    ) {
      defaultActiveApplicationId = activeApplicationBeforeSave.applicationId;
    } else {
      const newApplicationId = applicationIds.find((t) => !applicationIdsWithStatus.some((k) => k.applicationId === t));
      // eslint-disable-next-line no-extra-boolean-cast
      if (!!newApplicationId) {
        defaultActiveApplicationId = newApplicationId;
      }
    }
    return defaultActiveApplicationId;
  }

  // =========== Auto-Recovery: Start =========== //

  /**
   * Performs POST request to save current 1003 session state to Auto Recovery persistent store
   * in the event the user has unsaved changes when losing a session
   */
  autoRecoverySave1003(callbackFn?: any) {
    const snapshotUrl = `${this._environment.retail.apiUrl}/snapshot`;
    const payload: UpsertSnapshotEntryRequest = { fileDataId: this._fileDataId, value: this._assembleSnapshotValue() };
    const failedAutoRecoveryAttempts = this._sessionStore.getValue().failedAutoRecoveryAttempts ?? 0;

    if (this.isAutoRecoveryStale() && failedAutoRecoveryAttempts < this._environment.retail.maxAutoRecoveryAttempts) {
      this._httpClient
        .put(snapshotUrl, payload, { headers: CmgUiUtilities.getSkipSpinnerHeaders() })
        .pipe(
          catchError((error) => {
            this._sessionStore.update({ failedAutoRecoveryAttempts: failedAutoRecoveryAttempts + 1 });
            return this._handleError(error, Cmg1003Api.AutoRecoverySave1003);
          }),
          tap((snapshot: UpsertSnapshotEntryResponse) => {
            this._sessionStore.update({
              lastAutoRecoverySaveTime: new Date(snapshot.lastModified).valueOf(),
              failedAutoRecoveryAttempts: 0,
            });
          }),
        )
        .subscribe();

      if (callbackFn && typeof callbackFn === 'function') {
        callbackFn();
      }
    }
  }

  /**
   * Performs DELETE request to delete the temporary Auto-Recovery persistent save
   * state based on the userId-fileDataId key
   */
  autoRecoveryDelete1003(deleteAll: boolean, callbackFn?: any) {
    const snapshotUrl = `${this._environment.retail.apiUrl}/snapshot?fileDataId=${this._fileDataId}&deleteAll=${deleteAll}`;

    this._sessionStore.update({ lastAutoRecoverySaveTime: null });
    this._httpClient
      .delete(snapshotUrl, { headers: CmgUiUtilities.getSkipSpinnerHeaders() })
      .pipe(catchError((e) => this._handleError(e, Cmg1003Api.AutoRecoveryDelete1003)))
      .subscribe();

    if (callbackFn && typeof callbackFn === 'function') {
      callbackFn();
    }
  }

  /**
   * Check to determine if last Auto-Recovery save is outdated based on recent form updates
   */
  isAutoRecoveryStale(): boolean {
    const lastUpdatedDateTime = this._sessionQuery.getValue().lastUpdatedDateTime;
    const lastAutoRecoverySaveTime = this._sessionQuery.getValue().lastAutoRecoverySaveTime;
    return !lastAutoRecoverySaveTime || lastUpdatedDateTime > lastAutoRecoverySaveTime;
  }

  // ==== START: Notes ==== //
  getLeadNotes = (guid: string) => this._httpClient.get<LeadNote>(`${this._environment.retail.apiUrl}/leads/${guid}/notes`);

  saveLeadNotes = (request: LeadNote) => this._httpClient.put(`${this._environment.retail.apiUrl}/leads/${request.leadGUID}/notes`, request);

  getLoanNotes(fileDataId: number): Observable<LoanNotesModel> {
    return this._httpClient
      .post<LoanNotesModel[]>(`${this._environment.retail.apiUrl}/loannotes/search/byids`, [fileDataId])
      .pipe(
        map((notes) => notes[0]),
        tap((note) => {
          note = !note ? ({} as LoanNotesModel) : note;
          note.content = !note.content ? '' : note.content;
          this._loanNotesStore.update(note);
          this._loanNotesStore.preInitialize();
          this._loanNotesQuery.dirtyChecker2.dirtyChecker.setHead();
        }),
      )
      .pipe(catchError((e) => this._handleError(e, Cmg1003Api.GetLoanNotes)));
  }

  saveLoanNotes(): Observable<any> {
    const note = this._loanNotesQuery.getValue();
    if (note?.byteFileDataID) {
      return this._httpClient
        .put(`${this._environment.retail.apiUrl}/loannotes/batch`, [note])
        .pipe(catchError((e) => this._handleError(e, Cmg1003Api.SaveLoanNotes)));
    }

    return of(null);
  }

  /*  saveLoanApplicationNotes(guid: string): Observable<any> {
    const note = this._loanNotesQuery.getValue();
    if (!note?.byteFileDataID) {
      return this._httpClient
        .post(`${this._environment.retail.apiUrl}/api/v1/loanapplications/${guid}/notes`, note)
        .pipe(catchError((e) => this._handleError(e, Cmg1003Api.SaveLoanApplicationNotes)));
    }

    return of(null); // Return an observable when there are no notes to save
  } */

  // For future ticket: https://cmgfi.atlassian.net/browse/URLA2-1215.
  getLoanApplicationNotes = (guid: string) =>
    this._httpClient.get<LoanApplicationNote[]>(`${this._environment.retail.apiUrl}/loanapplications/${guid}/notes`);

  saveLoanApplicationNotesPost = (request: LoanApplicationNote) =>
    this._httpClient.post(`${this._environment.retail.apiUrl}/loanapplications/${request.loanApplicationGUID}/notes`, request);

  saveLoanApplicationNotesPut = (request: LoanApplicationNote) =>
    this._httpClient.put(`${this._environment.retail.apiUrl}/loanapplications/${request.loanApplicationGUID}/notes/${request?.guid}`, request);
  // ==== END: Notes ==== //

  /**
   * Returns the All 1003 Payload for saving the Application data
   */
  private _assembleSnapshotValue() {
    const activeApplicationId = this._storeQuery.getActiveApplicationId();
    const activeBorrowerIds = this._storeQuery.getBorrowersByApplicationId(activeApplicationId);
    const allBorrowerIds = this._storeQuery.getBorrowersByApplicationId(activeApplicationId, true);

    const all1003Payload = {
      fileDataId: this._fileDataId,
      applications: [this._storeQuery.getApplicationById(activeApplicationId)],
      assetsLiabilities: this._assetsQuery.getPersistentState(activeApplicationId),
      borrowers: this._borrowerQuery.getAll({
        filterBy: (b) => allBorrowerIds.some((bId) => bId === b.borrowerId),
      }),
      declarations: this._declarationsQuery.getValueByBorrowerIds(allBorrowerIds),
      employmentExpenses: this._employmentExpensesQuery.getValue(),
      employmentIncomeExpenses: [this._employmentIncomeExpensesQuery.assembleForSave(activeApplicationId, true)],
      fileData: this._fileDataQuery.getValue(),
      governmentMonitors: this._gmiQuery.getValueByBorrowerIds(allBorrowerIds),
      loanInformation: this._loanQuery.getValue(),
      loanOfficer: this._loanOfficerQuery.getValue(),
      property: this._propertyQuery.assemblePropertyForSave(activeBorrowerIds),
      transactionInfo: this._transactionQuery.assembleForSave(),
      aus: this._ausQuery.getValue(),
      coverLetterSubmissionNotes: this._coverLetterSubmissionsQuery.getValue(),
      dirtyStores: this._storeQuery.getDirtySections(),
    };

    return all1003Payload;
  }

  private _saveFirstTimeHomeBuyerStatusToFileData(): void {
    function convertIsFirstTimeHomeBuyerToNumber(isFirstTimeHomeBuyer: boolean | undefined) {
      if (isFirstTimeHomeBuyer === undefined || isFirstTimeHomeBuyer === null) {
        return 0;
      }

      if (isFirstTimeHomeBuyer) {
        return 1;
      }

      return 2;
    }

    this._borrowerQuery
      .selectAll()
      .pipe(
        take(1),
        tap((borrowers) => {
          let isFirstTimeHomeOwner: boolean | undefined;

          // TODO - This needs to be simplified / refactored.
          // eslint-disable-next-line no-unreachable-loop
          for (const { isFirstTimeHomeBuyer } of borrowers) {
            if (isFirstTimeHomeBuyer) {
              isFirstTimeHomeOwner = true;
              return;
            }

            isFirstTimeHomeOwner = isFirstTimeHomeBuyer;
            return;
          }

          this._fileDataStore.update({ firstTimeHomeBuyer: convertIsFirstTimeHomeBuyerToNumber(isFirstTimeHomeOwner) });
        }),
      )
      .subscribe();
  }

  private _updateAntiSteeringByLoanChannel() {
    const originationChannel = this._loanQuery.getValue()?.originationChannel;
    if (originationChannel !== OriginationChannel.brokeredOut) {
      this._loanStore.deleteAntiSteering();
    }
  }

  /**
   * If cached 1003 snapshot is available, this initiates the auto-recovery decision flow:
   * - `Unsaved Changes` OR `Last Saved Changes`
   */
  private _awaitAutoRecoveryDecision(all1003: Cmg1003Model) {
    const isValidSnapshot = !!all1003?.cacheData?.snapshot?.Value && !all1003?.cacheData?.cacheException;

    if (!isValidSnapshot) {
      this._sessionStore.update({ needsAutoRecoveryDecision: false });
      return;
    }

    // Setting `needsAutoRecoveryDecision` to `true` opens the auto-recovery modal,
    // via cmg-1003.component.ts: _subscribeToAutoRecovery().
    this._sessionStore.update({
      needsAutoRecoveryDecision: true,
      cached1003: all1003?.cacheData,
      lastSavedDateTime: all1003?.fileData?.dateModified || all1003?.cacheData?.snapshot?.Warning?.Timestamp,
    });

    // Should trigger when user chooses "Unsaved Changes" or "Last Saved Changes" within auto-recovery modal.
    this._sessionQuery
      .select((t) => t.needsAutoRecoveryDecision)
      .pipe(
        filter((needsDecision) => !needsDecision),
        take(1),
        tap(() => {
          this._updateStoresForAutoRecovery();
        }),
      )
      .subscribe();
  }

  /**
   * Updates 1003 data, based on the selected auto-recovery option:
   * - `Unsaved Changes` OR `Last Saved Changes`
   */
  private _updateStoresForAutoRecovery() {
    const snapshot1003 = this._sessionStore.getValue().cached1003?.snapshot?.Value;

    // [Scenario]: "Last Saved Changes" - i.e. the snapshot was deleted from store.
    if (!snapshot1003) {
      this.autoRecoveryDelete1003(false);
      return;
    }

    // [Scenario]: "Unsaved Changes" - i.e. the snapshot is still available in store.
    applyTransaction(() => {
      const activeApplicationId = snapshot1003.applications[0].applicationId;

      // [Scenario]: Snapshot contains a new application that hasn't been created in our Akita stores.
      if (!this._applicationInfoStore.getValue().ids.includes(activeApplicationId)) {
        const propertyTaxCalcType = getPropertyTaxCalculationType(this._empIncExpQuery);
        this._applicationInfoStore.addNewApplication(propertyTaxCalcType as PropertyTaxCalculationType, snapshot1003.applications[0]);
      }

      // Application
      this._applicationInfoStore.update(activeApplicationId, snapshot1003.applications[0]);

      // Borrowers
      snapshot1003.borrowers.forEach((bor) => {
        this._borrowerStore.update(bor.borrowerId, bor);
      });

      // Employment / Expenses
      this._employmentExpensesStore.update(snapshot1003.employmentExpenses);

      // Employment, Income & Expenses
      this._employmentIncomeExpensesStore.update(activeApplicationId, snapshot1003.employmentIncomeExpenses[0]);

      // Property & Gifts
      this._propertyStore.update(snapshot1003.property);

      // Loan
      // [Important]: Order matters! Sections dependent on Loan (e.g. Expenses) must be updated first.
      this._loanStore.update(snapshot1003.loanInformation);

      // Assets, Credits & Liabilities
      this._assetLiabilitiesStore.update(activeApplicationId, snapshot1003.assetsLiabilities[0]);

      // Transaction
      this._transactionStore.update(snapshot1003.transactionInfo);

      // Declarations
      snapshot1003.declarations.forEach((dec) => {
        this._declarationsStore.update(dec.borrowerId, dec);
      });

      // GMI & Military
      snapshot1003.governmentMonitors.forEach((gmi) => {
        this._gmiStore.update(gmi.borrowerId, gmi);
      });
      this._loanOfficerStore.update(snapshot1003.loanOfficer);
      this._fileDataStore.update(snapshot1003.fileData);
      this._coverLetterSubmissionsStore.update(snapshot1003.coverLetterSubmissionNotes);

      // Set active application
      this._applicationInfoStore.setActive(activeApplicationId);
    });
  }

  // =========== Auto-Recovery: End =========== //

  get fileDataId(): number {
    return this._fileDataId;
  }

  set fileDataId(v: number) {
    // We may need to add more edge cases here as the error can happen any time any where.
    if (
      this._fileDataId !== v /* When fileDataId changes  */ ||
      (this._fileDataId === v && !this._sessionQuery.getValue().all1003Loaded) /* When last fileDataId didn't load */
    ) {
      // When fileDataId changes, get the next valid token and make the call to get 1003.
      this._fileDataId = v;
      this._sessionStore.update({ fileDataId: v });
      this.reset();
    }
  }

  getMsaCounties(stateAbbreviation: string): Observable<{ msaCountyID: number; name: string }[]> {
    return this._httpClient.get<MsaCounties[]>(`${this._environment.retail.apiUrl}/aus/msacounties?stateAbbreviation=${stateAbbreviation}`).pipe(
      tap((msaCounties) => {
        this._ausQuery.dirtyChecker2.dirtyChecker.setHead();
        this._ausStore.update({ msaCounties });
        this._ausQuery.dirtyChecker2.dirtyChecker.setHead();
      }),
      catchError((err) => this._on1003Error(err)),
    );
  }

  getUrlaSnapshotHistoryModalRows(): Observable<UrlaSnapshotHistoryRow[]> {
    return this._httpClient
      .get<UrlaSnapshotHistoryRow[]>(`${this._environment.retail.apiUrl}/urlatransactionlogs/${this._fileDataId}/snapshot-history-modal-rows`)
      .pipe(catchError((err) => this._on1003Error(err)));
  }

  reportAnIssueWithUrlaSave(issueDescription: string) {
    const loanNumber = this._fileDataStore.getValue().fileName;
    return this._httpClient.post(`${this._environment.retail.apiUrl}/urlatransactionlogs/${this._fileDataId}/report-an-issue`, {
      loanNumber,
      issueDescription,
    });
  }

  // // =========== Loan Status History =========== //

  getLoanStatusHistory(fileDataId: number) {
    const url = `${this._environment.retail.apiUrl}/all1003/loanStatusHistory/${fileDataId}`;
    this._httpClient
      .get<loanStatusInfo>(url)
      .pipe(catchError((err) => this._on1003Error(err)))
      .subscribe((loanStatusInfo: loanStatusInfo) => {
        this._loanStatusHistoryStore.update(loanStatusInfo);
        this._loanStatusHistoryQuery.dirtyChecker.setHead();
      });
  }

  navigateToPageNotAllowed(params?: PageNotAllowedParams) {
    if (params) {
      this._router.navigate(['/405'], { state: { params: { ...params } } });
    } else {
      this._router.navigate(['/405']);
    }
  }

  navigateToSandboxNotAllowed(fileDataId: number) {
    this.navigateToPageNotAllowed({
      title: 'This service is not available for sandbox loan',
      buttonText: 'View Pipeline',
      buttonAction: PageNotAllowedActions.GoToPipeline,
      fileDataId: fileDataId,
    });
  }

  isSandboxDisabledRoute(path: string): boolean {
    const disabledRoutes = [
      '/closing-cost-worksheet-2-0',
      '/closing-cost-worksheet/fee-worksheet',
      '/disclosures',
      '/4506c',
      '/documents/management',
      '/documents/request',
      '/documents/pending',
      '/documents/review',
      '/conditions',
      '/loan-history-snapshot',
      '/parties',
      '/coverletter',
      '/conversationlog',
      '/letter',
      '/importantdate',
      '/voa',
      '/voe',
    ];

    if (!this.isSandboxLoanMVP2Enabled) {
      disabledRoutes.push('/aus');
      disabledRoutes.push('/closing-cost-worksheet');
      disabledRoutes.push('/closing-cost-worksheet-1-5');
    }

    for (const route of disabledRoutes) {
      if (path?.toLocaleLowerCase().includes(route)) {
        return true;
      }
    }

    return false;
  }

  navigateToNotAllowedPages(fileDataId: number, loanProgram: string) {
    this.navigateToPageNotAllowed({
      title: `This service is not accessible for ${loanProgram}`,
      buttonText: 'View Pipeline',
      buttonAction: PageNotAllowedActions.GoToPipeline,
      fileDataId: fileDataId,
    });
  }

  isNFTYDisabledRoute(path: string) {
    const disabledRoutes = [
      '/loansummary',
      '/credit/request',
      '/pricing',
      '/closing-cost-worksheet',
      '/closing-cost-worksheet-1-5',
      '/closing-cost-worksheet-2-0',
      '/closing-cost-worksheet/fee-worksheet',
      '/disclosures',
      '/documents/management',
      '/documents/request',
      '/documents/pending',
      '/documents/review',
      '/conditions',
      '/loan-history-snapshot',
      '/parties',
      '/coverletter',
      '/conversationlog',
      '/letter',
      '/importantdate',
      '/aus',
      '/voa',
      '/voe',
      '/buydown',
      '/1008',
      '/notes',
    ];

    for (const route of disabledRoutes) {
      if (path?.toLocaleLowerCase().includes(route)) {
        return true;
      }
    }

    return false;
  }
}
